// frontend/src/components/LegalAcceptanceModal.js

import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Checkbox,
  FormControlLabel,
  List,
  ListItem,
  ListItemText,
  Divider,
  Alert,
  Link as MuiLink
} from '@mui/material';
import { Link } from 'react-router-dom';
import axios from '../api/axiosConfig';

/**
 * Modal that blocks access when Terms/Privacy versions change
 * Shows changelog and requires user to accept before continuing
 */
export default function LegalAcceptanceModal({ open, versionDetails, onAccepted }) {
  const [accepted, setAccepted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleAccept = async () => {
    if (!accepted) {
      setError('You must accept the updated Terms and Privacy Policy to continue');
      return;
    }

    try {
      setLoading(true);
      setError('');

      // Granular acceptance - only update what changed
      await axios.post('/api/auth/accept-legal', {
        acceptTerms: needsTermsAcceptance,
        acceptPrivacy: needsPrivacyAcceptance
      });

      // Notify parent component to refresh and allow access
      onAccepted();

    } catch (err) {
      console.error('Legal acceptance error:', err);
      setError(err.response?.data?.error || 'Failed to update acceptance. Please try again.');
      setLoading(false);
    }
  };

  if (!versionDetails) return null;

  const { needsTermsAcceptance, needsPrivacyAcceptance, termsChangelog, privacyChangelog } = versionDetails;

  return (
    <Dialog
      open={open}
      maxWidth="md"
      fullWidth
      disableEscapeKeyDown
      PaperProps={{
        sx: { borderRadius: 3 }
      }}
    >
      <DialogTitle>
        <Typography variant="h5" fontWeight={700}>
          📋 Legal Updates Required
        </Typography>
      </DialogTitle>

      <DialogContent>
        <Alert severity="info" sx={{ mb: 3 }}>
          We've updated our legal documents. Please review the changes and accept to continue using Blackmount.
        </Alert>

        {needsTermsAcceptance && termsChangelog && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="h6" fontWeight={600} gutterBottom>
              🔹 Terms of Service Updates
            </Typography>
            <List dense>
              {termsChangelog.map((change, idx) => (
                <ListItem key={idx}>
                  <ListItemText primary={`• ${change}`} />
                </ListItem>
              ))}
            </List>
            <MuiLink
              component={Link}
              to="/terms"
              target="_blank"
              sx={{ fontSize: '0.9rem', fontWeight: 500 }}
            >
              Read full Terms of Service →
            </MuiLink>
          </Box>
        )}

        {needsPrivacyAcceptance && privacyChangelog && (
          <Box sx={{ mb: 3 }}>
            <Divider sx={{ my: 2 }} />
            <Typography variant="h6" fontWeight={600} gutterBottom>
              🔹 Privacy Policy Updates
            </Typography>
            <List dense>
              {privacyChangelog.map((change, idx) => (
                <ListItem key={idx}>
                  <ListItemText primary={`• ${change}`} />
                </ListItem>
              ))}
            </List>
            <MuiLink
              component={Link}
              to="/privacy-policy"
              target="_blank"
              sx={{ fontSize: '0.9rem', fontWeight: 500 }}
            >
              Read full Privacy Policy →
            </MuiLink>
          </Box>
        )}

        <Divider sx={{ my: 3 }} />

        <FormControlLabel
          control={
            <Checkbox
              checked={accepted}
              onChange={(e) => setAccepted(e.target.checked)}
              color="primary"
            />
          }
          label={
            <Typography variant="body2">
              I have reviewed and accept the updated{' '}
              {needsTermsAcceptance && 'Terms of Service'}
              {needsTermsAcceptance && needsPrivacyAcceptance && ' and '}
              {needsPrivacyAcceptance && 'Privacy Policy'}
            </Typography>
          }
        />

        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 3, pt: 0 }}>
        <Button
          fullWidth
          variant="contained"
          size="large"
          onClick={handleAccept}
          disabled={loading}
          sx={{ borderRadius: 3, py: 1.5 }}
        >
          {loading ? 'Updating...' : 'Accept and Continue'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
